import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ListTodo, Plus, AlertTriangle, CalendarClock, CalendarDays, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { TaskRow } from '@/components/tasks/TaskRow';
import { TaskDialog } from '@/components/tasks/TaskDialog';
import { useTasks } from '@/components/tasks/useTasks';
import { taskBucket } from '@/lib/taskUtils';

const TABS = [
  { key: 'overdue', label: 'Overdue', icon: AlertTriangle },
  { key: 'today', label: 'Today', icon: CalendarClock },
  { key: 'upcoming', label: 'Upcoming', icon: CalendarDays },
  { key: 'done', label: 'Done', icon: CheckCircle2 },
];

function DigestToggle() {
  const [enabled, setEnabled] = useState(true);
  useEffect(() => { API.get('/task-digest-preference').then(r => setEnabled(r.data.enabled)).catch(() => {}); }, []);
  const change = async (v) => {
    setEnabled(v);
    try { await API.put('/task-digest-preference', { enabled: v }); toast.success(v ? 'Daily email summary on' : 'Daily email summary off'); }
    catch { toast.error('Failed to save'); }
  };
  return (
    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
      <Switch data-testid="digest-toggle" checked={enabled} onCheckedChange={change} /> Email me a summary every morning (7am ET)
    </label>
  );
}

export default function TasksPage() {
  const { tasks, loading, reload, toggle, remove, snooze } = useTasks();
  const [tab, setTab] = useState('today');
  const [dialog, setDialog] = useState(null);
  const counts = tasks.reduce((acc, t) => { const b = taskBucket(t); acc[b] = (acc[b] || 0) + 1; return acc; }, {});
  const shown = tasks.filter(t => taskBucket(t) === tab);

  useEffect(() => { if (!loading && counts.overdue && tab === 'today' && !counts.today) setTab('overdue'); }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="tasks-page" className="space-y-5 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><ListTodo className="h-6 w-6 text-[#0073ea]" /> Tasks</h1>
          <p className="text-sm text-gray-500 mt-0.5">Call, text and follow-up reminders for your leads and buyers</p>
        </div>
        <Button data-testid="add-task-btn" onClick={() => setDialog({})} className="bg-[#0073ea] hover:bg-[#0060c2] text-white"><Plus className="h-4 w-4 mr-1.5" /> New Task</Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-0 no-scrollbar">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`tasks-tab-${key}`} onClick={() => setTab(key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 border transition-colors ${tab === key ? 'bg-[#0073ea] text-white border-[#0073ea]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
              <span data-testid={`tasks-count-${key}`} className={tab === key ? 'text-white/80' : key === 'overdue' && counts.overdue ? 'text-red-600 font-bold' : 'text-gray-400'}>{counts[key] || 0}</span>
            </button>
          ))}
        </div>
        <DigestToggle />
      </div>
      {loading ? <div className="h-40 rounded-xl bg-gray-100 animate-pulse" /> : shown.length === 0 ? (
        <p data-testid="no-tasks" className="text-sm text-gray-400 text-center py-16">Nothing here. Nice work.</p>
      ) : (
        <div className="space-y-1.5">{shown.map(t => <TaskRow key={t.id} task={t} onToggle={toggle} onEdit={(x) => setDialog({ task: x })} onDelete={remove} onSnooze={snooze} />)}</div>
      )}
      <TaskDialog open={!!dialog} onOpenChange={v => !v && setDialog(null)} task={dialog?.task} onSaved={reload} />
    </div>
  );
}
